// src/components/layout/Footer.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import socialLinks from '../../config/socialLinks';
import { FiTwitter, FiLinkedin, FiGithub, FiFacebook, FiInstagram } from 'react-icons/fi';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const navigateToTop = (path) => {
    navigate(path);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const footerSections = [
    {
      title: 'AI Agents',
      links: [
        { name: 'Sophie - Admin Assistant', href: '/agent/sophie' },
        { name: 'Emma - Marketing Expert', href: '/agent/emma' },
        { name: 'Léa - Legal Advisor', href: '/agent/lea' },
        { name: 'View All Agents', href: '/team' },
      ],
    },
    {
      title: 'Solutions',
      links: [
        { name: 'Team Packages', href: '/packages' },
        { name: 'How It Works', href: '/how-it-works' },
        { name: 'Integrations', href: '/integrations' },
        { name: 'Pricing', href: '/pricing' },
      ],
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'Contact', href: '/contact' },
        { name: 'Blog', href: '/blog' },
        { name: 'FAQ', href: '/faq' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { name: 'Documentation', href: '/docs' },
        { name: 'API Reference', href: '/api' },
        { name: 'Status', href: '/status' },
        { name: 'Community', href: '/community' },
      ],
    },
  ];

  const socialIcons = [
    { name: 'Twitter', href: socialLinks.twitter, icon: FiTwitter },
    { name: 'LinkedIn', href: socialLinks.linkedin, icon: FiLinkedin },
    { name: 'GitHub', href: socialLinks.github, icon: FiGithub },
    { name: 'Facebook', href: socialLinks.facebook, icon: FiFacebook },
    { name: 'Instagram', href: socialLinks.instagram, icon: FiInstagram },
  ];

  return (
    <footer className="bg-gray-950 dark:bg-surface-950 border-t border-gray-800/50 dark:border-white/[0.04]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-400 to-accent-400 flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="text-lg font-bold text-white tracking-tight">AniVyo</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              Specialized AI agents working together to transform every aspect of your business operations.
            </p>
            <div className="flex items-center gap-3">
              {socialIcons.map(({ name, href, icon: Icon }) => (
                <a
                  key={name}
                  href={href}
                  className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={name}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-4">
                {section.title}
              </h3>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => navigateToTop(link.href)}
                      className="text-sm text-gray-500 hover:text-gray-200 transition-colors text-left"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800/50 dark:border-white/[0.04] mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              &copy; {currentYear} AniVyo. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {[
                { name: 'Privacy', href: '/privacy-policy' },
                { name: 'Terms', href: '/terms' },
                { name: 'Cookies', href: '/cookies' },
              ].map(item => (
                <button
                  key={item.name}
                  onClick={() => navigateToTop(item.href)}
                  className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
